import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from "bootstrap-4-react/lib/components";
import LoaderSpinner from '../common/Loader';
import { 
    WALMART_AUTH_URL, 
    WALMART_ADAPTER_URL,
    WALMART_CLIENT_ID
} from '../../services/walmart.service';

// Walmart OAuth Configuration
const WALMART_REDIRECT_URI = `${WALMART_ADAPTER_URL}/auth/walmart/callback`;

/**
 * Generate a random nonce for OAuth security
 * @returns {string} Random alphanumeric string
 */
const generateNonce = () => {
    return Math.random().toString(36).substring(2, 12).toUpperCase();
};

/**
 * Generate a random state for OAuth state management
 * @returns {string} Random alphanumeric string
 */
const generateState = () => {
    return Math.random().toString(36).substring(2, 12).toUpperCase();
};

/**
 * WalmartLogin Component
 * Landing page for Walmart-initiated OAuth flow
 * 
 * When a seller clicks "Connect" on Walmart's Application Listing page,
 * Walmart redirects to this page with:
 * - walmartCallbackUri: The Walmart authorization URL
 * - clientType: seller (US), seller-ca (Canada), seller-mx (Mexico)
 * 
 * Flow:
 * 1. Check if user is logged in
 * 2. If not logged in - show login/signup options
 * 3. After login - automatically redirect to Walmart OAuth
 */
const WalmartLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        // Store Walmart OAuth params for after login
        const walmartCallbackUri = searchParams.get('walmartCallbackUri');
        const clientType = searchParams.get('clientType') || 'seller';
        
        if (walmartCallbackUri) {
            sessionStorage.setItem('walmart_callback_uri', walmartCallbackUri);
        }
        sessionStorage.setItem('walmart_client_type', clientType);
        sessionStorage.setItem('walmart_pending_connect', 'true');

        // Check if user is already logged in
        const userDetails = localStorage.getItem('userDetails');
        if (userDetails) {
            // Auto-proceed to Walmart OAuth
            proceedToWalmartOAuth(clientType, walmartCallbackUri);
        } else {
            setIsLoading(false);
        }
    }, [searchParams]);

    const proceedToWalmartOAuth = (clientType, walmartCallbackUri) => {
        setIsRedirecting(true);
        
        const nonce = generateNonce();
        const state = generateState();

        // Store state for validation
        sessionStorage.setItem('walmart_oauth_state', state);
        
        // Clear the pending connect flag
        sessionStorage.removeItem('walmart_pending_connect');

        // Use Walmart's callback URI if provided, otherwise use default
        const authUrl = walmartCallbackUri || WALMART_AUTH_URL;
        const marketClientType = clientType || 'seller';

        // Construct the authorization URL
        const fullAuthUrl = `${authUrl}?responseType=code&clientId=${WALMART_CLIENT_ID}&redirectUri=${WALMART_REDIRECT_URI}&nonce=${nonce}&state=${state}&clientType=${marketClientType}`;

        console.log('Walmart Login - Redirecting to:', fullAuthUrl);
        
        // Redirect to Walmart authorization
        window.location.href = fullAuthUrl;
    };

    const handleLogin = () => {
        // Navigate to login page - after login, user should be redirected back
        // The login component should check for walmart_pending_connect and redirect
        navigate('/login', { state: { returnTo: '/walmart/login' } });
    };

    const handleSignUp = () => {
        // Navigate to signup page
        navigate('/user-signup', { state: { returnTo: '/walmart/login' } });
    };

    if (isLoading || isRedirecting) {
        return (
            <main className="walmart-login-page">
                <div className="main__container">
                    <div className="login-content">
                        <div className="login-card" style={{ 
                            textAlign: 'center', 
                            padding: '60px 40px',
                            background: 'white',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                        }}>
                            <img 
                                src="https://cdn.mos.cms.futurecdn.net/5StAbRHLA4ZdyzQZVivm2c.jpg" 
                                alt="Walmart" 
                                style={{ maxWidth: '150px', marginBottom: '24px' }}
                            />
                            <h2 style={{ marginBottom: '16px', color: '#333' }}>
                                {isRedirecting ? 'Connecting to Walmart' : 'Loading...'}
                            </h2>
                            <p style={{ color: '#666', marginBottom: '24px' }}>
                                {isRedirecting 
                                    ? 'Please wait while we redirect you to Walmart Seller Center...'
                                    : 'Checking your session...'
                                }
                            </p>
                            <LoaderSpinner />
                        </div>
                    </div>
                </div>

                <style>{`
                    .walmart-login-page {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #0071dc 0%, #004c91 100%);
                    }
                    
                    .login-content {
                        width: 100%;
                        max-width: 500px;
                        padding: 20px;
                    }
                `}</style>
            </main>
        );
    }

    // User is not logged in - show login/signup options
    return (
        <main className="walmart-login-page">
            <div className="main__container">
                <div className="login-content">
                    <div className="login-card" style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                    }}>
                        <img 
                            src="https://cdn.mos.cms.futurecdn.net/5StAbRHLA4ZdyzQZVivm2c.jpg" 
                            alt="Walmart" 
                            style={{ maxWidth: '120px', marginBottom: '20px' }}
                        />
                        
                        <h2 style={{ marginBottom: '12px', color: '#333' }}>
                            Connect Walmart to 3PL Vision
                        </h2>
                        
                        <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>
                            To connect your Walmart Seller account, please log in or create an account first.
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <Button
                                className="cus-primary-bg-btn"
                                onClick={handleLogin}
                                style={{ width: '100%', marginBottom: '12px', padding: '12px' }}
                            >
                                Log In
                            </Button>
                            
                            <Button
                                className="cus-secondary-bg-btn"
                                onClick={handleSignUp}
                                style={{ width: '100%', padding: '12px' }}
                            >
                                Create Account
                            </Button>
                        </div>

                        <p style={{ color: '#999', fontSize: '12px' }}>
                            After logging in, you'll be automatically redirected to authorize your Walmart account.
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
                .walmart-login-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #0071dc 0%, #004c91 100%);
                }
                
                .login-content {
                    width: 100%;
                    max-width: 400px;
                    padding: 20px;
                }
            `}</style>
        </main>
    );
};

export default WalmartLogin;
